import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getPlatformMethodologies } from 'api/platformMethodologies';
import { getPlatformPractices } from 'api/platformPractices';
import { Flex, Line, Spinner } from 'components';
import { BackButton } from 'components/BackButton';
import { Button } from 'components/_form';
import { H1 } from 'components/Typography/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { showPlatformApiError } from 'views/PlatformView/platformApiError';
import { IPlatformMethodology } from 'types/platformResources';
import { Wrapper } from 'views/AdministrationView/views/CompanyAdministrationView/CompanyAdministrationView.styled';
import { AddPlatformMethodologyForm } from './components/AddPlatformMethodologyForm/AddPlatformMethodologyForm';
import { EditPlatformMethodologyForm } from './components/EditPlatformMethodologyForm/EditPlatformMethodologyForm';
import { PlatformMethodologiesTable } from './components/PlatformMethodologiesTable/PlatformMethodologiesTable';

export const PlatformResourcesView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [methodologies, setMethodologies] = useState<IPlatformMethodology[]>([]);
  const [practiceCounts, setPracticeCounts] = useState<Record<number, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<number>();

  const loadData = useCallback(async () => {
    setIsLoading(true);

    try {
      const [methodologiesRes, practicesRes] = await Promise.all([
        getPlatformMethodologies(),
        getPlatformPractices()
      ]);

      setMethodologies(methodologiesRes.data);

      const counts = practicesRes.data.reduce<Record<number, number>>((acc, practice) => {
        const mid = practice.methodology?.id;
        if (mid) acc[mid] = (acc[mid] || 0) + 1;
        return acc;
      }, {});
      setPracticeCounts(counts);
    } catch (error) {
      showPlatformApiError(error, t);
      setPracticeCounts({});
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const breadcrumb = useMemo(
    () => `${t('platform.title')} | ${t('platform.resources.title')}`,
    [t]
  );

  return (
    <Wrapper mb={94} flexDirection="column">
      <Flex width="100%" alignItems="center">
        <BackButton />
        <H1 m={2} variant={['h3', 'h2']} textTransform="uppercase">
          {breadcrumb}
        </H1>
        <Flex ml="auto">
          <Button variant="eucalyptus" minWidth="200px" onClick={() => setIsAddOpen(true)}>
            <FontAwesomeIcon icon={faPlusCircle} style={{ marginRight: '8px' }} />
            {t('platformResources.addMethodology')}
          </Button>
        </Flex>
      </Flex>
      <Line />

      {isLoading ? (
        <Spinner />
      ) : (
        <PlatformMethodologiesTable
          methodologies={methodologies}
          practiceCounts={practiceCounts}
          onOpen={(m) => navigate(`/app/platform/resources/methodologies/${m.id}`)}
          onEdit={(m) => setEditingId(m.id)}
        />
      )}

      <AddPlatformMethodologyForm
        isOpen={isAddOpen}
        onCancelClick={() => setIsAddOpen(false)}
        onSuccess={() => {
          setIsAddOpen(false);
          loadData();
        }}
      />

      {editingId && (
        <EditPlatformMethodologyForm
          isOpen={!!editingId}
          methodologyId={editingId}
          onCancelClick={() => setEditingId(undefined)}
          onSubmitSuccess={() => {
            setEditingId(undefined);
            loadData();
          }}
          onDeleteSuccess={() => {
            setEditingId(undefined);
            loadData();
          }}
        />
      )}
    </Wrapper>
  );
};
